import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import { fetchPricing, formatPrice, Pricing } from '../api/pricing';
import { PriceBreakdown } from './PriceBreakdown';
import { ALL_ROLES, AccountRole, ROLE_DESCRIPTIONS, ROLE_LABELS } from '../types/auth';

/**
 * The very first thing a person sees whenever they have no active role — before
 * worrying about starting or joining any program. Nothing else is reachable until
 * this resolves; see {@code RequireAuth}'s {@code requireActiveAccount}. That covers
 * two different situations with the same screen: a true first-timer (`user.role` is
 * null) picking a role for the first time, and a returning person whose yearly
 * subscription has lapsed (`user.role` is still set, just expired) — the copy and
 * the preselected role adjust for which one this is, but both submit through the
 * same {@code selectRole} call.
 *
 * <p>Picking a role here is the only thing this screen does — it does not also start
 * a program. Once resolved, an Organizer/Head Coach starts theirs from Program
 * Management like anyone else who already has access (see {@code ProgramSwitcher}),
 * free, since the role itself is what was paid for here. Every other role is granted
 * by an existing program's organizer, once this person holds it — see {@code
 * AccountService#addOrUpdateMember}.
 */
export function RoleSelectionModal() {
  const { user, selectRole } = useAuth();
  // Distinguishes a true first-timer from a returning person whose subscription has
  // actually lapsed — not just "role is set," which is also true while a first-ever
  // paid selection is still mid-checkout (roleExpiresAt stays null until activated).
  const isRenewal = user?.roleExpiresAt != null && user.roleExpiresAt < Date.now();
  const [pricing, setPricing] = useState<Pricing | null>(null);
  const [role, setRole] = useState<AccountRole>(user?.role ?? 'owner');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPricing().then(setPricing).catch(() => {});
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await selectRole(role);
      if (result.status !== 'active' && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
      // Active case needs no further action — needsRoleSelection flips to false on
      // the next render and this whole modal stops rendering on its own.
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not select that role.');
    } finally {
      setBusy(false);
    }
  }

  const price = pricing ? formatPrice(pricing.amountDueCents[role]) : null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(4,12,24,0.92)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(96,200,232,0.28)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.55)',
        }}
      >
        <form onSubmit={handleSubmit} className="p-6">
          <h2
            className="text-white font-bold text-lg mb-1"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.03em' }}
          >
            {isRenewal ? 'Renew your access' : 'Choose your access'}
          </h2>
          <p className="text-sm mb-5" style={{ color: 'rgba(147,197,253,0.7)' }}>
            {isRenewal
              ? 'Your access has expired — pick a level to continue:'
              : 'Pick the access level you would like:'}
          </p>

          <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: 'rgba(147,197,253,0.7)' }}>
            Access level
          </label>
          <select
            value={role}
            onChange={e => { setRole(e.target.value as AccountRole); setError(''); }}
            className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
          >
            {ALL_ROLES.map(value => (
              <option key={value} value={value} className="bg-blue-950 text-white">
                {ROLE_LABELS[value]}
              </option>
            ))}
          </select>
          <p className="text-xs mb-1" style={{ color: 'rgba(147,197,253,0.6)' }}>
            {ROLE_DESCRIPTIONS[role]}
          </p>
          {pricing && <PriceBreakdown pricing={pricing} role={role} />}

          <button
            type="submit"
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            {busy
              ? (isRenewal ? 'Renewing…' : 'Selecting…')
              : `${isRenewal ? 'Renew' : 'Select'}${price ? ` — ${price}` : ''}`}
          </button>
          {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
        </form>
      </div>
    </div>
  );
}
